import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import secureStorage from '../services/secureStorage';

export const REPAIR_ID_KEY = 'selected_repair_id';
export const SHOW_REPAIR_HISTORY_KEY = 'show_repair_history';

/**
 * The task edit in progress on the Tasks tab: the resolution tapped and the
 * comment typed on the open task card, before Save. Published by TasksView.
 */
export interface TaskEdit {
  stepNumber: number;
  resultId?: number;
  resultName?: string;
  comment: string;
}

/**
 * The Repairs list as it is on screen: the Shop Scope dropdown and the search
 * box. Published by WorkOrdersScreen, sent to Nova AI as
 * `screen_context.repair_list`, so "switch to completed repairs" answers about
 * the list the technician is looking at rather than a default one.
 */
export interface RepairListView {
  /** Scope key: "my_repairs_my_shop" … "all_repairs_all_shops" */
  scope: string;
  /** The status tab showing: "All" | "Open" | "Closed" */
  status?: string;
  /** The priority dropdown's code ("UR"), absent when it shows All */
  priority?: string;
  /** Search column ("asset number", "document id", …), when a search is active */
  searchField?: string;
  /** What is typed in the search box */
  searchValue?: string;
}

/**
 * One repair card on a repairs list, as Nova AI needs it: the id, the three
 * descriptions its name is built from, its status, and its repair code.
 */
export interface RepairListRow {
  repairId: number | string;
  actionDesc?: string;
  groupDesc?: string;
  componentDesc?: string;
  status?: string;
  actionID?: string;
  groupID?: string;
  componentID?: string;
}

/** The cards as Nova AI is sent them: only what it reads, in the order shown. */
export const repairListRowsOf = (
  cards: ReadonlyArray<Partial<RepairListRow> & {repairId: number | string}>,
): RepairListRow[] =>
  cards.map(card => ({
    repairId: card.repairId,
    actionDesc: card.actionDesc,
    groupDesc: card.groupDesc,
    componentDesc: card.componentDesc,
    status: card.status,
    actionID: card.actionID,
    groupID: card.groupID,
    componentID: card.componentID,
  }));

/** One row of the parts search, as `parts/search` returns it. */
export interface PartsSearchRow {
  partID: number;
  partNumber?: string;
  partName?: string;
  storeroom?: string;
  storeroomDesc?: string;
  availableQty?: number;
}

/** One ticked row of the Configured Part List, as Nova AI needs it. */
export interface ConfiguredSelectionPart {
  partId: number | string;
  name: string;
  qty: number;
  storeRoom: string;
}

export interface ConfiguredSelection {
  count: number;
  parts: ConfiguredSelectionPart[];
}

export interface PartsSearchView {
  /** What is typed in the search box. */
  term: string;
  /** The rows under it, in the order they are shown. */
  rows: PartsSearchRow[];
}

interface RepairState {
  selectedRepairId: string;
  /**
   * Work order of the open repair (its documentId), set by RepairDetailsScreen
   * once the repair loads and cleared whenever selectedRepairId changes. Sent to
   * Nova AI as screen_context.work_order_id so "change this work order's status"
   * resolves without Nova re-reading the repair.
   */
  selectedRepairWorkOrderId: string;
  /**
   * The open repair as its own card names it — "Wiring Repair - Auger - Assembly",
   * built from actionDesc/groupDesc/componentDesc the way WorkOrderTileView builds
   * it. Set by RepairDetailsScreen once the repair loads and cleared whenever
   * selectedRepairId changes. Sent to Nova AI as screen_context.repair_label so a
   * reply can name the repair the technician is looking at: the repair id Nova
   * used to read out appears on no screen in the app.
   */
  selectedRepairLabel: string;
  /**
   * Note open in the Notes view's modal (view or edit), set by NotesView and
   * cleared when the modal closes or the repair changes. Sent to Nova AI as
   * screen_context.note_id so "read me this note" / "who added this note"
   * resolve to the note on screen instead of the newest one.
   */
  selectedNoteId: string;
  /**
   * The unsaved task edit on the Tasks tab, or null when no card is being
   * edited. Cleared on save, cancel, task-list refresh or repair change. Sent to
   * Nova AI as screen_context.task_edit so "save tasks" commits exactly what is
   * on screen rather than a guess.
   */
  taskEdit: TaskEdit | null;
  /** The Repairs list on screen, or null when no such list is showing. */
  repairListView: RepairListView | null;
  /**
   * The repair cards showing, in the order they are shown — the Repairs tab's
   * list or Home's repairs section — or null when neither is on screen. Set by
   * RepairsSection, sent to Nova AI as screen_context.repair_rows, so "open the
   * second repair" means the second card rather than the second row of a list
   * Nova showed earlier in the chat.
   */
  repairListRows: RepairListRow[] | null;
  /**
   * The parts search on the repair's Parts tab — what is in the "Search or Scan
   * Part" box and the rows showing under it — or null when nothing is searched.
   * Sent to Nova AI as screen_context.parts_search so "request the first part
   * from the search result" means the rows the technician is looking at. Nova
   * keeps its own memory of a search *it* ran, which is a different search and
   * was the only one it could see.
   */
  partsSearchView: PartsSearchView | null;
  /**
   * What is ticked on the repair's Configured Part List, so Nova AI can read
   * the parts and quantities back before it submits them and then post the
   * request itself. Quantities are whatever the technician left in the boxes,
   * edits included. Null means the panel is not on screen, which is not the
   * same as nothing being ticked.
   */
  configuredSelection: ConfiguredSelection | null;
  repairStarted: boolean;
  repairEnded: boolean;
  refetchData: boolean;
  openCreateWorkOrder: boolean;
  openRepairFlow: boolean;
  /** True when an active flow step requires confirmation before tab switching. */
  isFlowProtected: boolean;
  /** True when the protected flow step is Step 4 — Repair Creation (vs Step 3 — WO Form). */
  isRepairCreationStep: boolean;
  /** True when the protected flow is the standalone Repair Flow (Create Repair step). */
  isStandaloneRepairFlow: boolean;
  /** True when the Repair History sub-view is active within RepairDetailsScreen. */
  showRepairHistory: boolean;
}

const initialState: RepairState = {
  selectedRepairId: '',
  selectedRepairWorkOrderId: '',
  selectedRepairLabel: '',
  selectedNoteId: '',
  taskEdit: null,
  repairListView: null,
  repairListRows: null,
  partsSearchView: null,
  configuredSelection: null,
  repairStarted: false,
  repairEnded: false,
  refetchData: false,
  openCreateWorkOrder: false,
  openRepairFlow: false,
  isFlowProtected: false,
  isRepairCreationStep: false,
  isStandaloneRepairFlow: false,
  showRepairHistory: false,
};

const repairSlice = createSlice({
  name: 'repair',
  initialState,
  reducers: {
    setSelectedRepairId(state, action: PayloadAction<string>) {
      // A different repair (or none) means the remembered work order is stale.
      if (action.payload !== state.selectedRepairId) {
        state.selectedRepairWorkOrderId = '';
        state.selectedRepairLabel = '';
        state.partsSearchView = null;
        state.configuredSelection = null;
        state.selectedNoteId = '';
        state.taskEdit = null;
      }
      state.selectedRepairId = action.payload;
      // Persist to secureStorage on all platforms (encrypted)
      if (action.payload) {
        secureStorage.setItem(REPAIR_ID_KEY, action.payload).catch(() => {});
      } else {
        secureStorage.removeItem(REPAIR_ID_KEY).catch(() => {});
      }
    },
    setSelectedRepairWorkOrderId(state, action: PayloadAction<string>) {
      state.selectedRepairWorkOrderId = action.payload;
    },
    setSelectedRepairLabel(state, action: PayloadAction<string>) {
      state.selectedRepairLabel = action.payload;
    },
    setRepairListView(state, action: PayloadAction<RepairListView | null>) {
      state.repairListView = action.payload;
    },
    setRepairListRows(state, action: PayloadAction<RepairListRow[] | null>) {
      state.repairListRows = action.payload;
    },
    setConfiguredSelection(state, action: PayloadAction<ConfiguredSelection | null>) {
      state.configuredSelection = action.payload;
    },
    setPartsSearchView(state, action: PayloadAction<PartsSearchView | null>) {
      state.partsSearchView = action.payload;
    },
    setTaskEdit(state, action: PayloadAction<TaskEdit | null>) {
      state.taskEdit = action.payload;
    },
    setSelectedNoteId(state, action: PayloadAction<string>) {
      state.selectedNoteId = action.payload;
    },
    setRepairStarted(state, action: PayloadAction<boolean>) {
      state.repairStarted = action.payload;
    },
    setRepairEnded(state, action: PayloadAction<boolean>) {
      state.repairEnded = action.payload;
    },
    setRefetchData(state, action: PayloadAction<boolean>) {
      state.refetchData = action.payload;
    },
    setOpenCreateWorkOrder(state, action: PayloadAction<boolean>) {
      state.openCreateWorkOrder = action.payload;
    },
    setOpenRepairFlow(state, action: PayloadAction<boolean>) {
      state.openRepairFlow = action.payload;
    },
    setFlowProtected(state, action: PayloadAction<boolean>) {
      state.isFlowProtected = action.payload;
    },
    setIsRepairCreationStep(state, action: PayloadAction<boolean>) {
      state.isRepairCreationStep = action.payload;
    },
    setIsStandaloneRepairFlow(state, action: PayloadAction<boolean>) {
      state.isStandaloneRepairFlow = action.payload;
    },
    setShowRepairHistory(state, action: PayloadAction<boolean>) {
      state.showRepairHistory = action.payload;
      if (action.payload) {
        secureStorage.setItem(SHOW_REPAIR_HISTORY_KEY, 'true').catch(() => {});
      } else {
        secureStorage.removeItem(SHOW_REPAIR_HISTORY_KEY).catch(() => {});
      }
    },
  },
});

export const { setSelectedRepairId, setSelectedRepairWorkOrderId, setSelectedRepairLabel, setPartsSearchView, setConfiguredSelection, setSelectedNoteId, setTaskEdit, setRepairListView, setRepairListRows, setRepairStarted, setRepairEnded, setRefetchData, setOpenCreateWorkOrder, setOpenRepairFlow, setFlowProtected, setIsRepairCreationStep, setIsStandaloneRepairFlow, setShowRepairHistory } =
  repairSlice.actions;
export default repairSlice.reducer;
